import { Component, ErrorInfo, ReactNode } from 'react';
import { logError } from '@/utils/errorLogger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    
    try {
      // Log the error to our error tracking system without user ID
      logError(
        error, 
        'frontend', 
        { 
          componentStack: errorInfo.componentStack,
          location: window.location.href
        }
      ).catch(e => console.error("Failed to log error:", e));
    } catch (loggingError) {
      console.error("Error in error logging:", loggingError);
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="p-6 bg-red-50 border border-red-200 rounded-md">
          <h2 className="text-xl font-bold text-red-700 mb-2">Something went wrong</h2>
          <details className="text-sm">
            <summary className="cursor-pointer text-red-700 mb-2">View error details</summary>
            <pre className="bg-white p-3 rounded text-xs overflow-auto max-h-64">
              {this.state.error?.toString() || 'Unknown error'}
            </pre>
          </details>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
